import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";

import JaneReportService from "../../../../services/JaneReportService";
import UtilityService from "../../../../services/UtilityService";
import { EXPECTED_REPORTS, SITE_PAIRING_WORDS } from "../../../../constants/JaneReports";

import WComponent from "../../../common/WComponent";
import FileDropZone from "../../../common/file_drop_zone/FileDropZone";
import Loader from "../../../common/loader/Loader";

import ChecksList, { CheckStatus } from "./ChecksList";
import JournalPanel from "./JournalPanel";
import JournalShapePanel from "./JournalShapePanel";
import ItemHeadsPanel from "./ItemHeadsPanel";
import PairingsPanel from "./PairingsPanel";

import ToolsSummaryCard from "../kit/ToolsSummaryCard";
import ToolsHeader from "../kit/ToolsHeader";
import ToolsCollapsiblePanel from "../kit/ToolsCollapsiblePanel";
import ToolsNotice from "../kit/ToolsNotice";

import "./jane-reports-tool.css";

class JaneReportsTool extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			isExtracting: false,
			extraction: null,
			files: [],
			errorMessage: "",
			settings: [],
			reportsNeeded: [],
			journalVersion: 0,
			statedPriorReceivable: undefined,
			statedReceivableAsAt: undefined
		};
	}

	onSettingsChanged = async ({ settings, reportsNeeded, wasJustSaved }) => {
		await this.update({ settings, reportsNeeded, journalVersion: this.state.journalVersion + 1 });

		const isReadOnAnOlderChoice = wasJustSaved && this.state.files.length > 0;
		if (isReadOnAnOlderChoice) {
			await this.readReports({ files: this.state.files });
		}
	};

	onPairingsChanged = async () => {
		await this.update({ journalVersion: this.state.journalVersion + 1 });
	};

	onPriorReceivableStated = async ({ amount }) => {
		await this.update({ statedPriorReceivable: amount, journalVersion: this.state.journalVersion + 1 });
	};

	onReceivableAsAtStated = async ({ date }) => {
		await this.update({ statedReceivableAsAt: date, journalVersion: this.state.journalVersion + 1 });
	};

	formatMoney = ({ amount }) => UtilityService.formatMoney({ value: amount });

	isSettingFilledFrom = ({ attribute, sourceColumn }) => {
		const { settings } = this.state;
		const setting = settings.find(candidate => candidate.attribute === attribute);
		const isFilledFrom = Boolean(setting && setting.isDecided && setting.sourceColumn === sourceColumn);
		return isFilledFrom;
	};

	readReports = async ({ files }) => {
		const { clientId } = this.props;

		await this.update({ isExtracting: true, errorMessage: "" });
		const extraction = await JaneReportService.extractReports({ clientId, files });

		if (extraction.error) {
			UtilityService.alert({ type: "error", text: extraction.error });
			await this.update({ isExtracting: false, extraction: null, errorMessage: extraction.error });
			return;
		}

		await this.update({ isExtracting: false, extraction, journalVersion: this.state.journalVersion + 1 });
	};

	onFilesSelected = async ({ fileList }) => {
		const files = Array.from(fileList);
		if (files.length === 0) {
			return;
		}

		await this.update({ files, extraction: null, statedPriorReceivable: undefined, statedReceivableAsAt: undefined });
		await this.readReports({ files });
	};

	onStartOver = async () => {
		await this.update({ extraction: null, files: [], errorMessage: "", statedPriorReceivable: undefined, statedReceivableAsAt: undefined });
	};

	revenueRowsOf = () => {
		const { extraction } = this.state;
		return extraction.figures[extraction.wiring.basis];
	};

	renderExpectedReport = ({ report }) => {
		const { extraction } = this.state;
		const received = extraction ? extraction.reports.find(entry => entry.key === report.key) : null;
		const isReceived = Boolean(received && received.received);

		return (
			<div className="tk-list__row" key={report.key}>
				<span className={clsx("jane-reports__mark", isReceived && "jane-reports__mark--received")}>
					{isReceived ? <Icon.Check size={13} /> : <Icon.Circle size={9} />}
				</span>
				<div className="tk-list__main">
					<div className="tk-list__title">{report.label}</div>
					<div className="tk-list__subtitle">{report.supplies}</div>
				</div>
				{isReceived && <span className="tk-list__trailing tk-text-muted">{received.fileName}</span>}
			</div>
		);
	};

	renderStaffRow = ({ entry }) => {
		const totalRevenue = this.revenueRowsOf().totalRevenue;
		const sharePercent = totalRevenue === 0 ? 0 : (entry.amount / totalRevenue) * 100;
		const rowClasses = clsx("tk-list__row", "tk-list__row--compact", "tk-list__row--hoverable", entry.isUnassigned && "tk-list__row--quiet");

		return (
			<div className={rowClasses} key={entry.staffName}>
				<span className="jane-reports__staff-name tk-list__main">{entry.staffName}</span>
				{entry.isUnassigned && <span className="tk-badge tk-badge--warning">No staff</span>}
				<span className="jane-reports__staff-share tk-text-muted">{sharePercent.toFixed(1)}%</span>
				<span className="jane-reports__staff-amount tk-amount">{this.formatMoney({ amount: entry.amount })}</span>
			</div>
		);
	};

	renderUnrecognisedFiles = ({ unrecognisedFiles }) => {
		if (unrecognisedFiles.length === 0) {
			return null;
		}

		return <ToolsNotice icon={<Icon.FileText size={16} />} detail={`Not recognised as a Jane report, so ignored: ${unrecognisedFiles.join(", ")}`} />;
	};

	renderError() {
		const { errorMessage } = this.state;

		if (!errorMessage) {
			return null;
		}

		return <ToolsNotice tone="danger" icon={<Icon.AlertTriangle size={16} />} title="These reports could not be read" detail={errorMessage} />;
	}

	renderUpload() {
		const { isExtracting } = this.state;

		if (isExtracting) {
			return (
				<div className="tk-panel-loading">
					<Loader />
					<span className="tk-text-muted">Reading the reports…</span>
				</div>
			);
		}

		return (
			<>
				{this.renderError()}
				<div className="tk-panel jane-reports__upload">
					<FileDropZone
						accept=".csv"
						multiple={true}
						title="Drop this month's Jane exports"
						subtitle="The CSV files listed here — they are identified by their columns, so the filenames don't matter"
						onFiles={fileList => this.onFilesSelected({ fileList })}
					/>
					<div>
						<div className="tk-section-label jane-reports__expected-heading">What this needs</div>
						<div className="tk-list tk-list--flush">
							{this.state.reportsNeeded.map(reportKey => this.renderExpectedReport({ report: EXPECTED_REPORTS.find(expected => expected.key === reportKey) }))}
						</div>
					</div>
				</div>
			</>
		);
	}

	renderFigures() {
		const { extraction } = this.state;
		const { billingSummary, accountsReceivable, payments } = extraction.figures;
		const revenueRows = this.revenueRowsOf();

		return (
			<div className="jane-reports__figures">
				<ToolsSummaryCard
					icon={<Icon.TrendingUp size={20} />}
					tone="success"
					title={extraction.basis.revenueTitle}
					value={this.formatMoney({ amount: revenueRows.totalRevenue })}
					meta={[{ text: `Includes ${this.formatMoney({ amount: revenueRows.totalTax })} tax` }, { text: `${revenueRows.rowCount} ${extraction.basis.rowNoun}` }]}
				/>
				<ToolsSummaryCard
					icon={<Icon.CornerUpLeft size={20} />}
					tone="danger"
					title="Refunds"
					value={this.formatMoney({ amount: billingSummary.totalRefunds })}
					meta={[
						{
							text: extraction.basis.booksRefunds
								? billingSummary.provenance.totalRefunds || "Not found"
								: "Not booked on the cash basis — it stays in the clearing account with the unapplied payments"
						}
					]}
				/>
				<ToolsSummaryCard
					icon={<Icon.CreditCard size={20} />}
					tone="danger"
					title="Merchant fees"
					value={this.formatMoney({ amount: payments ? payments.totalFees : null })}
					meta={[{ text: payments ? `${payments.transactionCount} transactions` : "No payments report" }]}
				/>
				{extraction.wiring.receivable && (
					<ToolsSummaryCard
						icon={<Icon.Clock size={20} />}
						tone="neutral"
						title="Accounts receivable"
						value={this.formatMoney({ amount: accountsReceivable ? accountsReceivable.totalOwing : null })}
						meta={[
							{
								text: accountsReceivable
									? `${accountsReceivable.patientCount} patients ${this.formatMoney({ amount: accountsReceivable.owingByPatients })} · ${accountsReceivable.otherPayerCount} insurers and other payers ${this.formatMoney({ amount: accountsReceivable.owingByOtherPayers })}`
									: "No report"
							}
						]}
					/>
				)}
			</div>
		);
	}

	renderCashBasisQuestions() {
		const { extraction } = this.state;
		const { billingSummary } = extraction.figures;
		const revenueRows = this.revenueRowsOf();

		return (
			<>
				<div className="jane-reports__question">
					<div className="tk-list__title">GST is coded on what was applied, not on what was invoiced</div>
					<div className="jane-reports__question-body">
						On this basis the tax codes carry {this.formatMoney({ amount: revenueRows.totalTax })} of tax, on the payments applied this month, while Jane
						invoiced {this.formatMoney({ amount: billingSummary.totalTax })} of tax. Under the Excise Tax Act GST is reportable when the consideration
						is paid or becomes due, whichever is earlier, so an invoice issued this month is reportable this month even if it is unpaid. Booking GST on
						the invoiced figure with an adjustment line is not available yet; until it is, the return needs that difference adjusted by hand.
					</div>
				</div>

				<div className="jane-reports__question">
					<div className="tk-list__title">Payments received but not yet applied stay in the clearing account</div>
					<div className="jane-reports__question-body">
						A payment Jane has received but not applied to an invoice is not revenue on this basis, and a refund pays one back, so both sit in the
						clearing account. The clearing summary below compares what this journal leaves there with what Jane received. Moving unapplied payments
						to a customer-deposits account is not available yet.
					</div>
				</div>

				<div className="jane-reports__question">
					<div className="tk-list__title">The year-end financial statements need revenue on an accrual basis</div>
					<div className="jane-reports__question-body">
						ASPE statements and corporate income are accrual, so at the fiscal year-end a cash-basis client's books need the year-end receivable added,
						less what the opening receivable already holds, as its own dated entry. Building that adjustment from the year-end Accounts Receivable
						export is not available yet.
					</div>
				</div>
			</>
		);
	}

	renderOpenQuestions() {
		const { extraction } = this.state;
		const { accountsReceivable } = extraction.figures;
		const revenueRows = this.revenueRowsOf();

		const hasReceivable = Boolean(accountsReceivable) && Boolean(extraction.wiring.receivable);
		const hasCutoffSpill = revenueRows.rowsInvoicedAfterPeriod > 0;

		return (
			<div className="jane-reports__questions">
				<div className="jane-reports__questions-heading">
					<Icon.AlertCircle size={15} />
					<span>Before this becomes a journal</span>
				</div>

				<div className="jane-reports__question">
					<div className="tk-list__title">Tax sits inside the revenue total</div>
					<div className="jane-reports__question-body">
						The {extraction.basis.revenueTitle.toLowerCase()} is inclusive of taxes and contains {this.formatMoney({ amount: revenueRows.totalTax })} of
						tax. The journal credits revenue without it and puts a tax code on each line, so QuickBooks records the liability rather than counting the
						tax as income.
					</div>
				</div>

				{extraction.basis.isCashBasis && this.renderCashBasisQuestions()}

				{hasCutoffSpill && (
					<div className="jane-reports__question">
						<div className="tk-list__title">Some of this period's sales were invoiced after it ended</div>
						<div className="jane-reports__question-body">
							The export covers purchases from {revenueRows.periodStart} to {revenueRows.periodEnd}, but{" "}
							{revenueRows.rowsInvoicedAfterPeriod} lines totalling {this.formatMoney({ amount: revenueRows.revenueInvoicedAfterPeriod })} carry an
							invoice date as late as {revenueRows.latestInvoiceDate}. Jane filters this report on the purchase date, so the revenue belongs to this
							period on a service-delivered basis — worth knowing if anyone reconciles against invoice dates instead.
						</div>
					</div>
				)}

				{hasReceivable && (
					<div className="jane-reports__question">
						<div className="tk-list__title">Receivables carry into next month and must be reversed</div>
						<div className="jane-reports__question-body">
							{this.formatMoney({ amount: accountsReceivable.totalOwing })} owed by {accountsReceivable.patientCount} patients and{" "}
							{accountsReceivable.otherPayerCount} insurers and other payers is the balance
							outstanding at the period end. It is posted whole, then reversed on the first day of the next month before that month's balance goes in
							— without the reversal, receivables accumulate month over month.
						</div>
					</div>
				)}
			</div>
		);
	}

	renderVerdict({ hasReconciled, detail }) {
		const startOverButton = (
			<button className="tk-button tk-button--secondary" onClick={this.onStartOver} type="button">
				Start over
			</button>
		);

		return (
			<ToolsNotice
				tone={hasReconciled ? "success" : "warning"}
				isCentered={true}
				icon={hasReconciled ? <Icon.CheckCircle size={18} /> : <Icon.AlertTriangle size={18} />}
				title={hasReconciled ? "The reports reconcile" : "These reports do not reconcile"}
				detail={hasReconciled ? detail : "A journal built from these figures would not balance. Check the exports cover the same month."}
				action={startOverButton}
			/>
		);
	}

	renderReconciliationPanel() {
		const { reconciliation } = this.state.extraction;
		const checkCount = reconciliation.checks.length;
		const failedChecks = reconciliation.checks.filter(check => !check.passed);
		const blockingFailureCount = failedChecks.filter(check => check.isBlocking !== false).length;
		const explainedDifferenceCount = failedChecks.length - blockingFailureCount;
		const hasNoBlockingFailure = blockingFailureCount === 0;

		let statusLabel = `All ${checkCount} reconciled`;
		if (!hasNoBlockingFailure) {
			statusLabel = `${blockingFailureCount} of ${checkCount} do not agree`;
		} else if (explainedDifferenceCount > 0) {
			const differenceWord = explainedDifferenceCount === 1 ? "difference" : "differences";
			statusLabel = `${checkCount - explainedDifferenceCount} reconciled, ${explainedDifferenceCount} explained ${differenceWord}`;
		}

		return (
			<ToolsCollapsiblePanel
				title="Reconciliation"
				defaultOpen={failedChecks.length > 0}
				hint={`Click to see all ${checkCount}`}
				status={<CheckStatus hasPassed={hasNoBlockingFailure} label={statusLabel} />}
			>
				<ChecksList checks={reconciliation.checks} leftHeading="This report says" rightHeading="Against" passedLabel="Reconciled" />
			</ToolsCollapsiblePanel>
		);
	}

	renderStaffPanel() {
		const { extraction } = this.state;
		const { reconciliation, basis } = extraction;
		const revenueRows = this.revenueRowsOf();
		const revenueCheck = reconciliation.checks.find(check => check.key === "revenueTiesToBillingSummary");
		const revenueCheckPassed = Boolean(revenueCheck && revenueCheck.passed);
		const revenueCheckLabel = revenueCheckPassed ? "Reconciled" : "Out";

		return (
			<ToolsCollapsiblePanel
				title="Revenue by staff member"
				hint={`Click to see all ${revenueRows.staffRevenue.length}`}
				status={
					<span className="jane-reports__collapsed-summary">
						<span className="tk-text-muted">
							{revenueRows.rowCount} {basis.rowNoun}
						</span>
						<span className="jane-reports__collapsed-amount tk-amount">{this.formatMoney({ amount: revenueRows.totalRevenue })}</span>
						<CheckStatus hasPassed={revenueCheckPassed} label={revenueCheckLabel} />
					</span>
				}
			>
				{revenueRows.staffWithNoRevenue.length > 0 && (
					<div className="jane-reports__section-note tk-text-muted">
						{revenueRows.staffWithNoRevenue.length} staff member with no net revenue, excluded from the journal
					</div>
				)}
				<div className="tk-list tk-list--flush tk-list--scroll">{revenueRows.staffRevenue.map(entry => this.renderStaffRow({ entry }))}</div>

				<div className="jane-reports__staff-total">
					<span className="jane-reports__staff-total-label">
						Total across {revenueRows.staffRevenue.length} staff members
						<span className="jane-reports__staff-total-note tk-text-muted"> · ties to the Billing Summary</span>
					</span>
					<span className="jane-reports__staff-amount tk-amount">{this.formatMoney({ amount: revenueRows.totalRevenue })}</span>
					<CheckStatus hasPassed={revenueCheckPassed} label={revenueCheckLabel} className="jane-reports__staff-total-status" />
				</div>
			</ToolsCollapsiblePanel>
		);
	}

	renderExtraction() {
		const { extraction, journalVersion, statedPriorReceivable, statedReceivableAsAt } = this.state;
		const { clientId } = this.props;
		const { reconciliation, figures, unrecognisedFiles, basis } = extraction;

		if (!basis) {
			return this.renderBasisUndecided();
		}

		const revenueRows = this.revenueRowsOf();

		if (!revenueRows) {
			return (
				<>
					{this.renderVerdict({ hasReconciled: false })}
					{this.renderUnrecognisedFiles({ unrecognisedFiles })}
					<ToolsNotice
						tone="warning"
						icon={<Icon.FileText size={16} />}
						title={`The ${extraction.missingReports.join(" and the ")} ${extraction.missingReports.length === 1 ? "is" : "are"} missing`}
						detail={`This client's revenue is recognised ${basis.label.toLowerCase()}, so the journal is built from the ${basis.rowNoun} in that export. Start over and drop it in with the others.`}
					/>
				</>
			);
		}

		const soldItems = revenueRows.soldItems;
		const janeLocations = revenueRows.locations;
		const isSplitByItem = this.isSettingFilledFrom({ attribute: "account", sourceColumn: "item" });
		const siteTarget = Object.keys(SITE_PAIRING_WORDS).find(target => this.isSettingFilledFrom({ attribute: "site", sourceColumn: target }));
		const siteWords = siteTarget ? SITE_PAIRING_WORDS[siteTarget] : null;

		return (
			<>
				{this.renderVerdict({
					hasReconciled: reconciliation.passed,
					detail: `${revenueRows.rowCount} ${basis.rowNoun} across ${janeLocations.length} ${janeLocations.length === 1 ? "location" : "locations"} agree with the Billing Summary's ${basis.billingColumn.toLowerCase()} total.`
				})}

				{this.renderUnrecognisedFiles({ unrecognisedFiles })}

				{extraction.missingReports.length > 0 && (
					<ToolsNotice
						tone="warning"
						icon={<Icon.FileText size={16} />}
						title={`Still needed: the ${extraction.missingReports.join(" and the ")} export`}
						detail="Every month is built from all five Jane exports — the journal posts from some and checks the others against the Billing Summary — so it cannot be built until this one is dropped in with the others."
						action={
							<button className="tk-button tk-button--secondary" onClick={this.onStartOver} type="button">
								Start over
							</button>
						}
					/>
				)}

				<div className="tk-panel">{this.renderFigures()}</div>

				{this.renderReconciliationPanel()}

				{!isSplitByItem && this.renderStaffPanel()}

				{siteWords && (
					<PairingsPanel
						key={`sites-${siteTarget}`}
						title={`Which QuickBooks ${siteWords.one} each site is`}
						lede="Jane and QuickBooks rarely spell a site the same way, so each pair is chosen once rather than matched on the words. A site left unpaired stops the journal instead of letting that site's revenue fall somewhere else."
						loadingText={`Reading this client's ${siteWords.many}…`}
						placeholder={`Choose a QuickBooks ${siteWords.one}…`}
						searchPlaceholder={`Search ${siteWords.many}…`}
						certaintyLabel="same name"
						doneWord="paired"
						todoWord="pair"
						noOptionsNotice={{
							title: `This client has no ${siteWords.many} in QuickBooks`,
							detail: `Jane reports ${janeLocations.length} sites. Either someone sets them up in QuickBooks, or change how this client's sites are recorded above.`
						}}
						loadRows={() => JaneReportService.listSitePairings({ clientId, janeLocations, target: siteTarget })}
						saveRows={({ pairings }) => JaneReportService.saveSitePairings({ clientId, target: siteTarget, pairings })}
						onChanged={this.onPairingsChanged}
					/>
				)}

				{isSplitByItem && <ItemHeadsPanel clientId={clientId} soldItems={soldItems} onChanged={this.onPairingsChanged} />}

				{reconciliation.passed && (
					<JournalPanel
						key={`journal-${journalVersion}`}
						clientId={clientId}
						figures={figures}
						revenueRows={revenueRows}
						uploadedReports={extraction.reports}
						statedPriorReceivable={statedPriorReceivable}
						statedReceivableAsAt={statedReceivableAsAt}
						onPriorReceivableStated={this.onPriorReceivableStated}
						onReceivableAsAtStated={this.onReceivableAsAtStated}
					/>
				)}

				{this.renderOpenQuestions()}
			</>
		);
	}

	renderBasisUndecided() {
		const { extraction } = this.state;
		const monthName = extraction.periodStart ? UtilityService.formatDate({ value: extraction.periodStart, format: "MMMM YYYY" }) : "this month";

		return (
			<>
				{this.renderUnrecognisedFiles({ unrecognisedFiles: extraction.unrecognisedFiles })}
				<ToolsNotice
					tone="warning"
					isCentered={true}
					icon={<Icon.HelpCircle size={18} />}
					title={`Choose when revenue is recognised for ${monthName}`}
					detail="Nobody has chosen yet whether this client's revenue counts when it is invoiced or when the payment is applied, so there is no telling which Jane export the journal comes from. Choose “Revenue recognised” above; the reports are matched again as soon as it is saved."
					action={
						<button className="tk-button tk-button--secondary" onClick={this.onStartOver} type="button">
							Start over
						</button>
					}
				/>
			</>
		);
	}

	render() {
		const { extraction } = this.state;
		const { clientId } = this.props;

		return (
			<div className="container">
				<div className="tk-surface jane-reports">
					<ToolsHeader
						icon={<Icon.FileText size={19} />}
						title="Jane Reports"
						subtitle="Reads a month of exports from Jane, extracts the figures a journal is built from, and checks they agree with each other before anything reaches QuickBooks."
					/>

					<div className="tk-stack">
						<JournalShapePanel clientId={clientId} periodStart={extraction ? extraction.periodStart : null} onChanged={this.onSettingsChanged} />

						{!extraction && this.renderUpload()}
						{extraction && this.renderExtraction()}
					</div>
				</div>
			</div>
		);
	}
}

export default JaneReportsTool;
